import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useFeatureGate from '../hooks/useFeatureGate';
import useFavorites from '../hooks/useFavorites';
import Header from '../components/Header';
import { formatDate } from '../utils/format';

/**
 * Returns the URL for a Twitch channel's avatar via the Glitch avatar proxy.
 * Falls back to an initials-based placeholder if slug is empty.
 */
function channelAvatarUrl(slug) {
  if (!slug) return null;
  // Use unavatar.io which provides a stable avatar proxy for Twitch usernames
  // e.g. https://unavatar.io/twitch/ninja
  return `https://unavatar.io/twitch/${encodeURIComponent(slug)}`;
}

export default function FavoritesPage() {
  const { user } = useAuth();
  const globalEnabled = useFeatureGate('feature:favorites');
  const tierEnabled = user?.tier?.features?.favorites?.channelsEnabled ?? false;
  const playbackEnabled = user?.tier?.features?.playback?.enabled ?? false;
  const { favorites = [], loading, error, addFavorite, removeFavorite, bulkRemoveFavorites, refresh } = useFavorites();

  const maxChannels = user?.tier?.features?.favorites?.maxChannels ?? 0;
  /* c8 ignore start */
  const currentCount = favorites?.length ?? 0;
  /* c8 ignore stop */
  /* c8 ignore start */
  const atLimit = maxChannels > 0 && currentCount >= maxChannels;
  /* c8 ignore stop */

  // Usage ratio for visual indicator (mirrors CollectionPage)
  const usageRatio = maxChannels > 0 ? currentCount / maxChannels : 0;
  const usageColor =
    usageRatio >= 1 ? 'text-red-400' :
    usageRatio >= 0.8 ? 'text-yellow-400' :
    'text-gray-400';

  // Default alphabetical order for favorites (display name, then channel name)
  /* c8 ignore start */
  const sortedFavorites = (favorites || [])
  /* c8 ignore stop */
    .slice()
    .sort((a, b) => {
      const an = (a.channel_display_name || a.channelDisplayName || a.channel_name || a.channelName || '').toLowerCase();
      const bn = (b.channel_display_name || b.channelDisplayName || b.channel_name || b.channelName || '').toLowerCase();
      return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
    });

  const [adding, setAdding] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [confirmRemoveSlug, setConfirmRemoveSlug] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const addInputRef = useRef(null);

  const normalizedQuery = (searchQuery || '').trim().toLowerCase();
  const filteredFavorites =
    normalizedQuery === ''
      ? sortedFavorites
      : sortedFavorites.filter((f) => {
          /* c8 ignore start */
          const name = (f.channel_name || f.channelName || '').toLowerCase();
          const display = (f.channel_display_name || f.channelDisplayName || '').toLowerCase();
          return name.includes(normalizedQuery) || display.includes(normalizedQuery);
          /* c8 ignore stop */
        });

  const isFilterActive = normalizedQuery !== '';

  if (!globalEnabled || !tierEnabled) {
    return (
      <div className="min-h-screen bg-dark-900 text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">Favorites Not Available</h1>
            <p className="text-gray-400 mb-6">
              Your current tier does not include access to the Favorite Channels feature.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-3 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const handleOpenAddInput = () => {
    setShowAddInput(true);
    setAddInput('');
    // Focus the input after render
    setTimeout(() => addInputRef.current?.focus(), 50);
  };

  const handleCancelAdd = () => {
    setShowAddInput(false);
    setAddInput('');
  };

  const handleConfirmAdd = async () => {
    const name = addInput.trim();
    if (!name) return;
    setAdding(true);
    await addFavorite(name.toLowerCase(), name);
    setAdding(false);
    setShowAddInput(false);
    setAddInput('');
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirmAdd();
    if (e.key === 'Escape') handleCancelAdd();
  };

  const toggleSelect = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const selectAll = () =>
    /* c8 ignore next */
    setSelected(new Set(filteredFavorites.map((f) => (f.channel_name || f.channelName || '').toLowerCase())));

  const deselectAll = () => setSelected(new Set());

  const invertSelection = () => {
    const next = new Set();
    filteredFavorites.forEach((f) => {
      /* c8 ignore start */
      const slug = (f.channel_name || f.channelName || '').toLowerCase();
      if (!selected.has(slug)) next.add(slug);
      /* c8 ignore stop */
    });
    setSelected(next);
  };

  const handleBulkRemove = async () => {
    /* c8 ignore start */
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} selected channel${selected.size === 1 ? '' : 's'} from favorites?`)) return;
    const names = Array.from(selected);
    const ok = await bulkRemoveFavorites(names);
    if (ok) setSelected(new Set());
    /* c8 ignore stop */
  };

  return (
    <div className="min-h-screen bg-dark-900 pb-12">
      <Header />

      <main className={`max-w-7xl mx-auto px-4 py-6 ${!loading && filteredFavorites.length > 0 ? 'pb-32' : ''}`}>
        {/* Page header — mirrors CollectionPage layout */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold"><span role="img" aria-label="Favorites">⭐</span> Favorite Channels</h1>
            <p className={`mt-1 ${usageColor}`}>
              <span data-testid="favorites-count">{currentCount}</span>
              {maxChannels > 0 ? ` / ${maxChannels} channels` : ' channels'}
              {usageRatio >= 1 && ' — Limit reached'}
            </p>
          </div>

          {/* Add Channel control */}
          <div className="flex items-center gap-2">
                {showAddInput ? (
              <>
                <input
                  ref={addInputRef}
                  type="text"
                  value={addInput}
                  onChange={(e) => setAddInput(e.target.value)}
                  onKeyDown={handleAddKeyDown}
                  placeholder="Channel name (e.g. ninja)"
                  className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent w-52"
                  aria-label="Channel name to add"
                  data-testid="add-channel-input"
                  disabled={adding}
                />
                <button
                  onClick={handleConfirmAdd}
                  disabled={adding || !addInput.trim()}
                  className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-add-button"
                >
                  {adding ? 'Adding…' : 'Add'}
                </button>
                <button
                  onClick={handleCancelAdd}
                  className="px-3 py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
                  data-testid="cancel-add-button"
                >
                  Cancel
                </button>
              </>
              ) : (
                <>
                  <button
                    onClick={refresh}
                    className="px-3 py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
                    aria-label="Refresh favorites"
                    title="Refresh"
                    data-testid="refresh-button"
                  >
                    ↻ Refresh
                  </button>
                  <button
                    onClick={handleOpenAddInput}
                    disabled={atLimit}
                    className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={atLimit ? 'Channel limit reached' : 'Add a channel to favorites'}
                    data-testid="add-channel-button"
                  >
                    + Add Channel
                  </button>
                  {/* Header Start Playback button when there are no favorites (disabled) */}
                  {playbackEnabled && favorites.length === 0 && (
                    <button
                      disabled
                      className="ml-2 px-4 py-2 bg-dark-600 text-gray-500 rounded-lg text-sm cursor-not-allowed"
                      data-testid="start-playback-button"
                      aria-label="Start playback (disabled: no favorites)"
                    >
                      ▶ Start Playback
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Search / Filter Bar */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by channel name…"
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent"
                aria-label="Search favorites"
                data-testid="favorites-search-input"
              />
            </div>
          </div>
          {isFilterActive && (
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span data-testid="filter-results-count">
                Showing {filteredFavorites.length} of {favorites.length} channels
              </span>
              <button
                onClick={() => setSearchQuery('')}
                className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                data-testid="clear-filter-button"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div
              className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"
              role="status"
              aria-label="Loading favorites"
            />
          </div>
        )}

        {/* Empty state — no channels at all */}
        {!loading && filteredFavorites.length === 0 && favorites.length === 0 && !error && (
          <div className="text-center py-20" data-testid="favorites-empty-state">
            <div className="text-5xl mb-4" role="img" aria-label="No favorites">⭐</div>
            <h2 className="text-xl font-semibold text-gray-300 mb-2">No favorite channels yet</h2>
            <p className="text-gray-400 mb-6">
              Add channels you follow to quickly access them from the Dashboard.
            </p>
            <button
              onClick={handleOpenAddInput}
              disabled={atLimit}
              className="inline-block px-6 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Channel
            </button>
          </div>
        )}

        {/* No results from filter */}
        {!loading && filteredFavorites.length === 0 && favorites.length > 0 && (
          <div className="text-center py-20" data-testid="favorites-no-results">
            <div className="text-5xl mb-4" role="img" aria-label="No results">🔍</div>
            <h2 className="text-xl font-semibold text-gray-300 mb-2">No channels match your filter</h2>
            <p className="text-gray-400 mb-6">Try adjusting your search or clearing the filter.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="inline-block px-6 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Channel card grid */}
        {!loading && filteredFavorites.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="favorites-grid"
          >
            {filteredFavorites.map((f) => {
              const displayName = f.channel_display_name || f.channelDisplayName || f.channel_name || f.channelName || '';
              const slug = (f.channel_name || f.channelName || '').toLowerCase();
              const isSelected = selected.has(slug);
              const isConfirmingRemove = confirmRemoveSlug === slug;
              // Prefer avatar provided by API (Twitch profile_image_url) then fall back to proxy
              const avatarUrl = f.profile_image_url || f.profileImageUrl || channelAvatarUrl(slug);

              return (
                <div
                  key={slug}
                  className={`bg-dark-800 border rounded-lg overflow-hidden transition-all ${
                    isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-dark-600'
                  }`}
                  data-testid="favorite-card"
                >
                  {/* Avatar banner */}
                  <div
                    className="relative flex items-center justify-center bg-dark-700 h-20 cursor-pointer group"
                    onClick={() => toggleSelect(slug)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSelect(slug);
                      }
                    }}
                    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${displayName}`}
                  >
                    {/* Selection indicator */}
                    <div
                      className={`
                        absolute top-2 left-2 w-6 h-6 rounded-full border-2 z-10
                        flex items-center justify-center transition-all
                        ${isSelected
                          ? 'bg-primary border-primary'
                          : 'bg-black/50 border-white/50 group-hover:border-white'
                        }
                      `}
                      aria-hidden="true"
                    >
                      {isSelected && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>

                    {/* Channel avatar */}
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={`${displayName} avatar`}
                        className="w-16 h-16 rounded-full object-cover border-2 border-dark-600 group-hover:border-primary/50 transition-colors"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    {/* Fallback initials avatar */}
                    <div
                      className={`w-16 h-16 rounded-full bg-primary/20 border-2 border-primary/30 flex items-center justify-center text-xl font-bold text-primary ${avatarUrl ? 'hidden' : ''}`}
                      aria-hidden="true"
                    >
                      {displayName.charAt(0).toUpperCase() || '?'}
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-3">
                    <h3 className="font-medium text-sm text-white truncate text-center" title={displayName}>
                      {displayName}
                    </h3>
                    <p className="text-xs text-gray-400 text-center mt-0.5">@{slug}</p>

                    {f.added_at && (
                      <p className="text-xs text-gray-500 text-center mt-1">
                        Added {formatDate(f.added_at, 'date')}
                      </p>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <a
                        href={`https://www.twitch.tv/${slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 text-gray-300 hover:text-white rounded-sm transition-colors"
                        aria-label={`Open ${displayName} on Twitch`}
                        title="Open on Twitch"
                      >
                        <span aria-hidden="true" className="mr-1">↗</span>
                          Open
                      </a>

                      {/* Remove / Confirm */}
                      {isConfirmingRemove ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setConfirmRemoveSlug(null);
                              removeFavorite(slug);
                            }}
                            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-sm transition-colors"
                            aria-label="Confirm remove"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmRemoveSlug(null)}
                            className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 text-gray-300 rounded-sm transition-colors"
                            aria-label="Cancel remove"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemoveSlug(slug)}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                          aria-label={`Remove ${displayName} from favorites`}
                          title="Remove from favorites"
                        >
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Fixed bottom action bar — mirrors CollectionPage bulk action bar; only shown when channels exist */}
      {!loading && filteredFavorites.length > 0 && (
      <div className="fixed bottom-0 left-0 right-0 z-50" role="region" aria-label="Channel selection actions" data-testid="bulk-action-bar">
        <div className="max-w-7xl mx-auto px-4 pb-4">
          <div className="bg-dark-700 border border-dark-500 rounded-xl shadow-2xl p-4">
            {/* Selection counter and bulk remove */}
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary font-bold"
                  aria-hidden="true"
                >
                  {selected.size}
                </div>
                <div aria-live="polite" aria-atomic="true">
                  <p className="font-medium text-white" data-testid="selected-count">
                    {selected.size === 0
                      ? 'No channels selected'
                      : `${selected.size} channel${selected.size !== 1 ? 's' : ''} selected`}
                  </p>
                </div>
              </div>

               <div className="flex items-center gap-2">
                 <button
                   onClick={handleBulkRemove}
                   disabled={selected.size === 0}
                   className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                     selected.size === 0
                       ? 'bg-dark-600 text-gray-500 cursor-not-allowed'
                       : 'text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/25'
                   }`}
                   aria-label={`Remove ${selected.size} selected channels`}
                   data-testid="bulk-remove-button"
                 >
                   Remove
                 </button>

                 {/* Start Playback button — visible when playbackEnabled for user's tier */}
                 {/* c8 ignore start */}
                 {playbackEnabled ? (
                   // If there are no favorites, show a disabled button
                   favorites.length === 0 ? (
                     <button
                       disabled
                       className="px-4 py-2.5 rounded-lg font-semibold bg-dark-600 text-gray-500 cursor-not-allowed"
                       data-testid="start-playback-button"
                       aria-label="Start playback (disabled: no favorites)"
                     >
                       ▶ Start Playback
                     </button>
                   ) : (
                     // If there are favorites, render a link that navigates to playback with selected or all channels
                     (() => {
                       /* c8 ignore start */
                       const channels = selected.size > 0
                         ? Array.from(selected)
                         : favorites.map((f) => (f.channel_name || f.channelName || '').toLowerCase()).filter(Boolean);
                       /* c8 ignore stop */
                       const query = channels.length > 0 ? `?channels=${channels.map(encodeURIComponent).join(',')}` : '';
                       return (
                         <Link
                           to={`/playback${query}`}
                           className="px-4 py-2.5 rounded-lg font-semibold bg-primary text-white hover:bg-primary-hover shadow-lg"
                           data-testid="start-playback-button"
                           aria-label="Start playback"
                         >
                           ▶ Start Playback
                         </Link>
                       );
                     })()
                   )
                 ) : null}
                 {/* c8 ignore stop */}
               </div>
              </div>

            {/* Selection control buttons */}
            <div className="flex items-center gap-2 pt-3 border-t border-dark-500">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                aria-label="Select all channels"
                data-testid="select-all-button"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                aria-label="Deselect all channels"
                data-testid="deselect-all-button"
              >
                Deselect All
              </button>
              <button
                onClick={invertSelection}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                aria-label="Invert selection"
                data-testid="invert-button"
              >
                Invert
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
